import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useEffect, useState } from 'react';
import { ArrowLeft, HandIcon, PenIcon } from 'lucide-react';
import { ButtonGroup, ButtonGroupItem } from '@/components/ui/ButtonGroup.tsx';
import { IconButton } from '@/components/ui/button';
import { useCreateJawsSegmentation } from '@hooks/useJawsSegmentation.ts';
import useSegmentationStore from '@segmentation/hooks/useSegmentationStore.ts';
import useThreeInteractionStore from '@modules/three/hooks/useThreeInteractionStore.ts';
import { InteractiveSplineToolEnum } from '@/modules/three/common/curve/interactiveSpline/InteractiveSplineToolEnum';
import { JawsSegmentationCreate } from '@schema/jawsSegmentation.ts';
import { useNavigate, useParams } from 'react-router-dom';
import { SegmentationWorkflowOverlay } from '@segmentation/UI/SegmentationWorkflowOverlay';
import { SEGMENTATION_UPLOAD_PHASE } from '@segmentation/segmentationProcessingPhases';
import { SegmentationPhase } from '@segmentation/store/segmentationStore.ts';
import { JawReviewPanel } from '@segmentation/UI/JawReviewPanel';

type EditStep = 'refine' | 'review';

interface SelectionEditProps {
  jaw: 'upper' | 'lower';
  setJaw: (jaw: 'upper' | 'lower') => void;
  setIsSelected: (isSegmented: boolean) => void;
}

export default function SelectionEdit({ jaw, setIsSelected, setJaw }: SelectionEditProps) {
  useDocumentTitle(`Braces Designer - Edit Selection | fullName`);
  const { jaws, reset, processingActive, setSegmentationPhase, update, scanId: storeScanId } = useSegmentationStore();
  const { editableLineTool, setEditableLineTool } = useThreeInteractionStore();
  const { mutate, isSuccess, isError, isPending } = useCreateJawsSegmentation();
  const navigate = useNavigate();
  const { scanId: routeScanId } = useParams<{ scanId: string }>();
  const scanId = routeScanId ?? storeScanId;

  const [step, setStep] = useState<EditStep>('refine');
  const [revertError, setRevertError] = useState<string | null>(null);

  const activeJawMesh = jaw === 'lower' ? jaws.lowerJaw : jaws.upperJaw;
  const jawTitle = jaw === 'lower' ? 'lower' : 'upper';

  async function handleFinishJawSegmentation() {
    setRevertError(null);
    try {
      activeJawMesh.saveRollbackCheckpoint(scanId);
      await jaws.cutTeeth(jaw);
      setStep('review');
    } catch (error) {
      setRevertError(error instanceof Error ? error.message : 'Segmentation failed.');
    }
  }

  async function handleBackToRefine() {
    setRevertError(null);
    try {
      jaws.revertJaw(jaw, scanId);
      update();
      setStep('refine');
    } catch (error) {
      setRevertError(error instanceof Error ? error.message : 'Could not restore previous contours.');
    }
  }

  async function handleContinueFromReview() {
    if (jaw === 'lower') {
      jaws.clearSegmentedTeethFromScene('lower');
      // Upper jaw goes through its own gum-edit phase first; point adding is
      // re-enabled by the TEETH_SELECTION_UPPER phase change afterwards.
      setSegmentationPhase(SegmentationPhase.GUM_EDIT_UPPER);
      setJaw('upper');
      setIsSelected(false);
      setStep('refine');
      return;
    }

    // The gum blobs cached by cutTeeth predate the review-step trim.
    await jaws.refreshGumModels();
    const jawsData = jaws.getJawData();
    const payload: JawsSegmentationCreate = {
      jawsScanId: scanId!,
      lowerJawData: jawsData.lowerJaw.data,
      upperJawData: jawsData.upperJaw.data,
    };
    mutate({
      data: payload,
      upperJawGumModel: jawsData.upperJaw.gumModel,
      lowerJawGumModel: jawsData.lowerJaw.gumModel,
      lowerJawTeethModels: jawsData.lowerJaw.teethModels,
      upperJawTeethModels: jawsData.upperJaw.teethModels,
    });
  }

  useEffect(() => {
    if (!isSuccess) return;
    const timer = globalThis.setTimeout(() => {
      goBack();
    }, 1100);
    return () => globalThis.clearTimeout(timer);
  }, [isSuccess, navigate]);

  const goBack = () => {
    navigate(-1);
    reset();
  };

  const handleToolChange = (tool: InteractiveSplineToolEnum) => {
    setEditableLineTool(tool);
  };

  const showUploadOverlay = isPending || isSuccess || isError;

  if (showUploadOverlay) {
    return (
      <>
        <SegmentationWorkflowOverlay
          open={isPending}
          eyebrow="Saving segmentation"
          title="Preparing segmentation"
          message={SEGMENTATION_UPLOAD_PHASE}
          variant="loading"
        />
        <SegmentationWorkflowOverlay
          open={isSuccess}
          title="Upload successful"
          message="Redirecting back to the patient in a moment…"
          variant="success"
        />
        <SegmentationWorkflowOverlay
          open={isError}
          title="Upload failed"
          message="Something went wrong while saving. Try again."
          variant="error"
          footer={
            <IconButton
              className="mt-2 w-full rounded-2xl border border-red-400/40 bg-red-500/20"
              variant="ghost-danger"
              icon={ArrowLeft}
              onClick={goBack}
            >
              Go back
            </IconButton>
          }
        />
      </>
    );
  }

  if (step === 'review') {
    return (
      <>
        <JawReviewPanel
          jaw={jaw}
          jawMesh={activeJawMesh}
          onBackToRefine={handleBackToRefine}
          onContinue={handleContinueFromReview}
          isContinuePending={isPending}
        />
        {revertError && (
          <p className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
            {revertError}
          </p>
        )}
      </>
    );
  }

  return (
    <section className="flex w-full max-w-sm flex-col items-center gap-6 rounded-3xl border border-primary/40 bg-content/80 p-6 text-white shadow-xl shadow-primary/20 backdrop-blur">
      <header className="space-y-3 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Edit selection</p>
        <h2 className="text-2xl font-semibold tracking-tight text-white">Refine the contour</h2>
        <p className="text-sm text-white/70">
          Adjust the spline by dragging control points or drawing new segments between them to smooth the jaw outline.
        </p>
      </header>

      <ButtonGroup
        value={editableLineTool}
        onChange={handleToolChange}
        className="flex flex-row gap-4 [&_button]:rounded-2xl [&_button]:border [&_button]:border-content-highlight/40 [&_button]:bg-content-dark/60 [&_svg]:h-6 [&_svg]:w-6"
      >
        <ButtonGroupItem size="3xl" className="w-20" icon={HandIcon} value={InteractiveSplineToolEnum.Dragging} />
        <ButtonGroupItem size="3xl" className="w-20" icon={PenIcon} value={InteractiveSplineToolEnum.Drawing} />
      </ButtonGroup>

      {revertError && (
        <p className="w-full rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
          {revertError}
        </p>
      )}

      <IconButton
        onClick={handleFinishJawSegmentation}
        disabled={processingActive}
        variant="ghost-primary"
        size="lg"
        className="w-full rounded-2xl border border-primary/50 bg-primary/80 text-sm font-semibold uppercase tracking-[0.25em] hover:bg-primary disabled:opacity-60"
      >
        {processingActive ? 'Processing…' : `Finish ${jawTitle} jaw segmentation`}
      </IconButton>
    </section>
  );
}
